import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Avatar } from '../Components/Avatar';
import { User } from '../Types/User';

interface ProfilePhotoProps {
  user: User;
  handleChange: (image: File) => void;
  showChangeText?: boolean;
}

const ProfilePhoto: React.FC<ProfilePhotoProps> = ({
  user,
  handleChange,
  showChangeText = true,
}) => {
  // State to hold the preview URL
  const [previewSrc, setPreviewSrc] = useState<string>(
    user.profileImage?.iconUrl,
  );

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageClick = (event: React.MouseEvent) => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const supportedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!supportedTypes.includes(file.type)) {
        toast.error(
          `File type: ${file.type} is not supported, we support jpeg, png and gif`,
        );
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File is too large, please select a file less than 5MB');
        return;
      }

      // Update the previewSrc state to display the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        const newSrc = e.target?.result as string;
        setPreviewSrc(newSrc);
      };
      reader.readAsDataURL(file);

      // Call the original handleChange prop with the file for uploading
      handleChange(file);
    }
  };

  return (
    <div className="flex gap-2 self-start text-base leading-6 text-center items-center text-blue-600">
      <Avatar
        user={user}
        src={previewSrc}
        size={80}
        onClick={handleImageClick}
      />
      <label
        title={'We support png, jpeg and gif up to 5MB'}
        className="cursor-pointer"
      >
        {showChangeText && 'Change Photo'}
        <input
          ref={fileInputRef}
          id="profile-image"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
      </label>
    </div>
  );
};

export default ProfilePhoto;
